import { useContext, useMemo, useState } from 'react';
import {
  useApplicationDetail,
  useCustomer,
  usePatchSMS,
  sms_communication_hooks,
  communication_template_hooks,
} from '@axo/shared/data-access/hooks';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { sms } from '@axo/shared/data-access/types';
import { convertToE164 } from '@axo/shared/util/formatters';

export type ISMSState = ISMSNew | ISMSOld;

export interface ISMSNew {
  sms: sms.CreateSMS;
  isNew: true;
}

export interface ISMSOld {
  sms: sms.SMS;
  isNew: false;
}

export function useSMSState(applicationID: string) {
  const { state } = useContext(DataAccessContext);
  const application = useApplicationDetail(applicationID);
  const customer = useCustomer(application.data?.CustomerID);
  const SMSList = sms_communication_hooks.useSMSList({
    customerPhoneNumber:
      convertToE164({
        phoneNumber: customer.data?.MobilePhoneNumber ?? '',
        phoneCountryCode: customer.data?.MobilePhoneCountryCode?.toString(),
      }) ?? '',
    filter: {
      applicationID: applicationID,
      limit: 50,
    },
  });
  const [SMSState, setSMSState] = useState<ISMSState[]>([]);
  const createSMS = sms_communication_hooks.useCreateCommunicationSMS(
    convertToE164({
      phoneNumber: customer.data?.MobilePhoneNumber ?? '',
      phoneCountryCode: customer.data?.MobilePhoneCountryCode?.toString() ?? '',
    }) ?? '',
  );
  const fillTemplate =
    communication_template_hooks.useFillCommunicationTemplate();
  const patchSMS = usePatchSMS();

  const intitialSMS: sms.CreateSMS = {
    LoanApplicationID: applicationID,
    MarketCountry: application.data?.MarketCountry || '',
    CreatorID: state.user.clientID,
    CustomerID: application.data?.CustomerID || '',
    Body: '',
    TemplateID: '',
    Transactional: false,
    Source: 'ACE',
    Meta: {
      Broker: application.data?.Meta?.Broker,
      Brand: application.data?.Meta?.Brand,
    },
  };

  const createInitialSMSState = (SMS: sms.SMS[]) => {
    return SMS.map((SMS) => ({
      sms: SMS,
      isNew: false,
    }));
  };

  useMemo(() => {
    if (SMSList.data?.Items) {
      setSMSState(createInitialSMSState(SMSList.data.Items));
    }
  }, [SMSList.data?.Items]);

  const updateSMSState = (
    compare: keyof ISMSState,
    originalState: ISMSState,
    updatedState: ISMSState,
  ) => {
    setSMSState((prevState) =>
      prevState.map((sms) => {
        if (sms[compare] === originalState[compare]) {
          return updatedState;
        } else {
          return sms;
        }
      }),
    );
  };

  return {
    state: SMSState,
    hasLoaded: SMSList.isSuccess,
    addNewSMS: (template: sms.SMSTemplate, loanQuoteID?: string) => {
      setSMSState((prevState) => [
        {
          sms: {
            ...intitialSMS,
            ID: new Date().toISOString(),
            Body: template.Body,
            DraftBody: template.Body,
            TemplateID: template.ID,
            LoanQuoteID: loanQuoteID,
            Transactional: template.Transactional,
          },
          isNew: true,
        },
        ...prevState,
      ]);
    },
    saveSMS: async (SMS: ISMSState) => {
      if (SMS.isNew) {
        fillTemplate
          .mutateAsync({
            LoanApplicationID: applicationID,
            LoanQuoteID: SMS.sms.LoanQuoteID ?? '',
            Body: SMS.sms.Body,
          })
          .then((response) => {
            const toCreate = {
              LoanApplicationID: SMS.sms.LoanApplicationID,
              MarketCountry: SMS.sms.MarketCountry,
              CreatorID: SMS.sms.CreatorID,
              CustomerID: SMS.sms.CustomerID,
              TemplateID: SMS.sms.TemplateID,
              Transactional: SMS.sms.Transactional,
              LoanQuoteID: SMS.sms.LoanQuoteID,
              FollowupAt: SMS.sms.FollowupAt,
              Body: response.Body,
              CustomerPhoneNumber:
                convertToE164({
                  phoneNumber: customer.data?.MobilePhoneNumber ?? '',
                  phoneCountryCode:
                    customer.data?.MobilePhoneCountryCode?.toString() ?? '',
                }) ?? '',
              Source: 'ACE',
              Meta: {
                Broker: application.data?.Meta?.Broker,
                Brand: application.data?.Meta?.Brand,
              },
            };
            createSMS.mutateAsync(toCreate).then((updated) =>
              updateSMSState('sms', SMS, {
                sms: updated,
                isNew: false,
              }),
            );
          });
      }
    },
    cancelSMS: (SMS: ISMSState) => {
      if (SMS.isNew) {
        setSMSState((prevState) =>
          prevState.filter((currentSMS) => currentSMS !== SMS),
        );
      }
    },

    editSMS: (SMS: ISMSState) => (editSMS: sms.CreateSMS) => {
      if (SMS.isNew) {
        updateSMSState('sms', SMS, {
          ...SMS,
          sms: editSMS,
        });
      }
    },
    markAsResolved: (SMS: ISMSState) => {
      if (!SMS.isNew) {
        const resolvedSMS: ISMSState = {
          ...SMS,
          sms: {
            ...SMS.sms,
            ResolvedAt: new Date().toISOString(),
          },
        };
        const { ID, FollowupAt, ResolvedAt } = resolvedSMS.sms;

        if (ID)
          patchSMS
            .mutateAsync({
              ID: ID,
              FollowupAt: FollowupAt,
              ResolvedAt: ResolvedAt,
            })
            .then(() => updateSMSState('sms', SMS, resolvedSMS));
      }
    },
  };
}
